/**
 * Slack connect step (plan slack-connector.md §7.1). Far simpler than WhatsApp
 * or Signal: there is no QR, no chat picker, and no history import. Slack has no
 * backfill path (D5) and indexes every conversation you're in automatically
 * (D6), so connecting IS indexing — the single action starts OAuth and hands the
 * browser to Slack's consent screen. The callback returns to
 * /sources?slack=connected, so this step never advances the wizard itself
 * (the OAuth redirect leaves the SPA before any onNext could fire).
 */
import { useMutation } from '@tanstack/react-query';
import { Loader2, Slack } from 'lucide-react';
import { toast } from 'sonner';
import { IconTile } from '@/components/patterns/icon-tile';
import { Button } from '@/components/ui/button';
import { ApiError, slackAccountApi } from '@/lib/api';
import { hardNavigate } from '@/lib/oauth';

export function StepSlackConnect() {
  const connect = useMutation({
    mutationFn: () => slackAccountApi.oauthStart(),
    onSuccess: ({ authorizeUrl }) => hardNavigate(authorizeUrl),
    onError: (err) => {
      if (err instanceof ApiError && err.status === 429) {
        toast.error("You've reached the Slack account limit.");
        return;
      }
      toast.error("Couldn't start Slack sign-in. Please try again.");
    },
  });

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border p-8">
        <IconTile tone="green">
          <Slack className="h-5 w-5" />
        </IconTile>
        <h2 className="mt-4 font-semibold text-lg">Connect Slack</h2>

        {/* §7.3 copy — VERBATIM (D1, the honest boundary; AC35). */}
        <div className="mt-4 space-y-3 text-[13.5px] text-muted-foreground">
          <p>
            <span className="font-semibold text-foreground">
              Brain indexes the Slack conversations you&apos;re in.
            </span>{' '}
            Every channel, DM, and group DM you&apos;re a member of is indexed automatically —
            there&apos;s nothing to pick. Slack only sends us conversations you&apos;ve joined, so
            public channels you haven&apos;t joined won&apos;t appear. Indexing starts from the
            moment you connect; earlier messages aren&apos;t imported.
          </p>
          <p>
            <span className="font-semibold text-foreground">
              This includes Slack Connect channels shared with other companies
            </span>{' '}
            — messages from people outside your organisation are indexed too, and are marked{' '}
            <em>External</em> in Sources.
          </p>
        </div>

        <Button
          size="cta"
          className="mt-6 w-full"
          disabled={connect.isPending}
          onClick={() => connect.mutate()}
        >
          {connect.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Connect Slack'}
        </Button>
      </div>
    </div>
  );
}
